import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { INTAKE_FORMS } from "../pages/intakeDefinitions";
import { trackEvent } from "../lib/tracking";

// ONE reusable native intake engine — the same component renders the newborn,
// infant, or toddler questionnaire verbatim from INTAKE_FORMS (the audited
// Google Form definitions). Answers autosave to the client's own paid record
// (Save & Continue Later), and final submit marks Intake Complete, fires the
// Intake Received email server-side, and hands back the scheduling URL.
// Answers are client information: they NEVER appear in tracking events.
const API = process.env.REACT_APP_BACKEND_URL;

// Gentle chapter pauses — visual reassurance only, never question content.
const PAUSES = [
  { at: 0.34, line: "Thank you — there's no perfect answer here. The real picture is what helps." },
  { at: 0.67, line: "You're doing beautifully. Just a little more." },
];

const isAnswered = (v) =>
  Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim() !== "";

export const IntakeForm = ({ stage, serviceType, sessionId, draft = {}, draftOthers = {}, parentName, onSubmitted }) => {
  const reduce = useReducedMotion();
  const form = INTAKE_FORMS[stage];
  const [answers, setAnswers] = useState(draft || {});
  const [others, setOthers] = useState(draftOthers || {}); // free text for "Other:" options
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const saveTimer = useRef(null);
  const firstName = (parentName || "").trim().split(" ")[0] || null;

  // Journey tracking — slugs and percents only, NEVER answers or PII.
  const THRESHOLDS = [25, 50, 75, 100];
  const initialAnswered = useMemo(
    () => form.questions.filter((q) => isAnswered((draft || {})[q.key])).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const startedRef = useRef(false);
  const resumedRef = useRef(false);
  const firedRef = useRef(new Set(THRESHOLDS.filter((t) => (initialAnswered / form.questions.length) * 100 >= t)));
  const journeyParams = () => ({ service_type: serviceType, client_stage: stage, intake_type: "native" });

  useEffect(() => {
    // Ref guard: dev StrictMode double-runs mount effects — fire exactly once.
    if (initialAnswered > 0 && !resumedRef.current) {
      resumedRef.current = true;
      trackEvent("intake_resumed", {
        ...journeyParams(),
        progress_percent: Math.round((initialAnswered / form.questions.length) * 100),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markInteraction = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("intake_started", journeyParams());
  };

  const answeredCount = useMemo(
    () => form.questions.filter((q) => isAnswered(answers[q.key])).length,
    [answers, form]
  );
  const total = form.questions.length;

  // intake_progress at 25/50/75/100 only — never one-event-per-keystroke.
  useEffect(() => {
    const pct = (answeredCount / total) * 100;
    for (const t of THRESHOLDS) {
      if (pct >= t && !firedRef.current.has(t)) {
        firedRef.current.add(t);
        if (startedRef.current) {
          trackEvent("intake_progress", { ...journeyParams(), progress_percent: t });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredCount]);
  const pauseAfter = useMemo(
    () =>
      Object.fromEntries(
        PAUSES.map((p) => [form.questions[Math.floor(total * p.at)]?.key, p.line])
      ),
    [form, total]
  );

  // Autosave — debounced; the parent's progress is never one tap from lost.
  // "Other:" free text autosaves too, so a half-typed note survives closing.
  useEffect(() => {
    if (!sessionId) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API}/api/journey/intake-draft`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, answers, others }),
        });
        if (resp.ok) setSavedAt(new Date());
      } catch {
        /* draft retries on the next keystroke — nothing is lost locally */
      }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, others]);

  const setValue = (key, value) => {
    markInteraction();
    setAnswers((a) => ({ ...a, [key]: value }));
  };

  const toggleChoice = (key, choice) => {
    markInteraction();
    setAnswers((a) => {
      const cur = Array.isArray(a[key]) ? a[key] : [];
      const on = cur.includes(choice);
      return { ...a, [key]: on ? cur.filter((c) => c !== choice) : [...cur, choice] };
    });
  };

  const setOtherText = (key, text) => {
    markInteraction();
    setOthers((o) => ({ ...o, [key]: text }));
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    trackEvent("intake_completed", journeyParams());
    // Fold any "Other:" free text into the answer itself so the stored
    // record reads exactly like the parent's own words.
    const finalAnswers = { ...answers };
    for (const q of form.questions) {
      const text = (others[q.key] || "").trim();
      if (!q.other || !text) continue;
      const v = finalAnswers[q.key];
      if (Array.isArray(v) && v.includes("__other__")) {
        finalAnswers[q.key] = [...v.filter((c) => c !== "__other__"), `Other: ${text}`];
      } else if (v === "__other__") {
        finalAnswers[q.key] = `Other: ${text}`;
      }
    }
    try {
      const resp = await fetch(`${API}/api/journey/intake-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, answers: finalAnswers }),
      });
      if (!resp.ok) throw new Error(String(resp.status));
      const data = await resp.json();
      onSubmitted(data.schedule_url || "/schedule-sleep-plan-review");
    } catch {
      setSubmitting(false);
      setSubmitError(true);
    }
  };

  const rise = {
    initial: reduce ? false : { opacity: 0, y: 14 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  };

  const optionRow = (selected) =>
    `flex items-center gap-3.5 w-full text-left rounded-2xl border px-5 py-4 text-base leading-relaxed transition-colors duration-200 ${
      selected
        ? "border-clay/60 bg-linen text-ink"
        : "border-ink/10 bg-ivory text-ink/80 hover:border-ink/25"
    }`;

  const dot = (selected, round) => (
    <span
      className={`shrink-0 flex items-center justify-center w-5 h-5 border transition-colors duration-200 ${
        round ? "rounded-full" : "rounded-md"
      } ${selected ? "border-clay bg-clay text-ivory" : "border-ink/25 text-transparent"}`}
    >
      <Check size={12} strokeWidth={3.5} />
    </span>
  );

  return (
    <div data-testid="native-intake-form">
      {/* Intro + progress */}
      <div className="max-w-xl">
        <p className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
          {firstName ? `${firstName}, take` : "Take"} your time — there are no
          perfect answers. Andrea needs the real picture, not the polished one.
        </p>
        <div className="mt-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-ink/10 relative overflow-visible">
            <div
              data-testid="intake-progress-bar"
              className="absolute left-0 -top-px h-[3px] rounded-full bg-clay/70 transition-[width] duration-500"
              style={{ width: `${Math.round((answeredCount / total) * 100)}%` }}
            />
          </div>
          <p data-testid="intake-progress-count" className="text-xs tracking-wide text-smoke whitespace-nowrap">
            {answeredCount} of {total} answered
          </p>
        </div>
        <p className="mt-3 flex items-center gap-2 text-xs text-euca">
          <Check size={12} strokeWidth={3} />
          Your progress is saved automatically.
          {savedAt && <span className="text-smoke/70">Saved {savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
        </p>
      </div>

      {/* Questions — original order, original wording */}
      <div className="mt-12 max-w-xl space-y-12">
        {form.questions.map((q, i) => {
          const value = answers[q.key];
          const otherSelected = q.other && (Array.isArray(value) ? value.includes("__other__") : value === "__other__");
          return (
            <div key={q.key}>
              <motion.fieldset {...rise} data-testid={`intake-q-${q.key}`} className="border-0 p-0 m-0">
                <legend className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">
                  <span className="text-clay/60 italic text-base mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {q.wording}
                </legend>
                <div className="mt-4">
                  {q.type === "short_answer" && (
                    <input
                      type="text"
                      data-testid={`intake-input-${q.key}`}
                      value={value || ""}
                      onChange={(e) => setValue(q.key, e.target.value)}
                      className="w-full rounded-2xl border border-ink/15 bg-ivory px-5 py-4 text-base text-ink placeholder:text-smoke/50 focus:border-clay focus:outline-none transition-colors duration-200"
                      placeholder="Type your answer here"
                    />
                  )}
                  {q.type === "paragraph" && (
                    <textarea
                      data-testid={`intake-input-${q.key}`}
                      value={value || ""}
                      onChange={(e) => setValue(q.key, e.target.value)}
                      rows={4}
                      className="w-full rounded-2xl border border-ink/15 bg-ivory px-5 py-4 text-base leading-relaxed text-ink placeholder:text-smoke/50 focus:border-clay focus:outline-none transition-colors duration-200"
                      placeholder="Share as much or as little as you like"
                    />
                  )}
                  {q.type === "date" && (
                    <input
                      type="date"
                      data-testid={`intake-input-${q.key}`}
                      value={value || ""}
                      onChange={(e) => setValue(q.key, e.target.value)}
                      className="w-full sm:w-auto rounded-2xl border border-ink/15 bg-ivory px-5 py-4 text-base text-ink focus:border-clay focus:outline-none transition-colors duration-200"
                    />
                  )}
                  {q.type === "radio" && (
                    <div className="space-y-2.5" role="radiogroup">
                      {q.choices.map((c) => (
                        <button
                          type="button"
                          key={c}
                          data-testid={`intake-opt-${q.key}-${c.slice(0, 20)}`}
                          onClick={() => setValue(q.key, value === c ? "" : c)}
                          className={optionRow(value === c)}
                          aria-pressed={value === c}
                        >
                          {dot(value === c, true)}
                          <span>{c}</span>
                        </button>
                      ))}
                      {q.other && (
                        <>
                          <button
                            type="button"
                            data-testid={`intake-opt-${q.key}-other`}
                            onClick={() => setValue(q.key, otherSelected ? "" : "__other__")}
                            className={optionRow(otherSelected)}
                            aria-pressed={otherSelected}
                          >
                            {dot(otherSelected, true)}
                            <span>Other</span>
                          </button>
                          {otherSelected && (
                            <input
                              type="text"
                              data-testid={`intake-other-${q.key}`}
                              value={others[q.key] || ""}
                              onChange={(e) => setOtherText(q.key, e.target.value)}
                              className="w-full rounded-2xl border border-ink/15 bg-ivory px-5 py-4 text-base text-ink focus:border-clay focus:outline-none transition-colors duration-200"
                              placeholder="Tell Andrea in your own words"
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {q.type === "checkboxes" && (
                    <div className="space-y-2.5">
                      {q.choices.map((c) => {
                        const on = Array.isArray(value) && value.includes(c);
                        return (
                          <button
                            type="button"
                            key={c}
                            data-testid={`intake-opt-${q.key}-${c.slice(0, 20)}`}
                            onClick={() => toggleChoice(q.key, c)}
                            className={optionRow(on)}
                            aria-pressed={on}
                          >
                            {dot(on, false)}
                            <span>{c}</span>
                          </button>
                        );
                      })}
                      {q.other && (
                        <>
                          <button
                            type="button"
                            data-testid={`intake-opt-${q.key}-other`}
                            onClick={() => toggleChoice(q.key, "__other__")}
                            className={optionRow(otherSelected)}
                            aria-pressed={otherSelected}
                          >
                            {dot(otherSelected, false)}
                            <span>Other</span>
                          </button>
                          {otherSelected && (
                            <input
                              type="text"
                              data-testid={`intake-other-${q.key}`}
                              value={others[q.key] || ""}
                              onChange={(e) => setOtherText(q.key, e.target.value)}
                              className="w-full rounded-2xl border border-ink/15 bg-ivory px-5 py-4 text-base text-ink focus:border-clay focus:outline-none transition-colors duration-200"
                              placeholder="Tell Andrea in your own words"
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </motion.fieldset>
              {pauseAfter[q.key] && (
                <motion.p
                  {...rise}
                  data-testid={`intake-pause-${q.key}`}
                  className="mt-14 mb-2 font-serif italic font-light text-clay text-lg sm:text-xl text-center"
                >
                  {pauseAfter[q.key]}
                </motion.p>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <div className="mt-16 max-w-xl">
        <p className="text-sm font-normal leading-relaxed text-smoke">
          When you submit, Andrea is notified right away and you&rsquo;ll go
          straight to scheduling your 45-minute Sleep Plan Review.
        </p>
        <button
          data-testid="intake-submit-btn"
          onClick={submit}
          disabled={submitting}
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-ink text-ivory font-medium px-10 py-5 text-lg hover:bg-espresso disabled:opacity-60 transition-colors duration-300 group"
        >
          {submitting ? "Sending to Andrea…" : "Submit My Intake"}
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        {submitError && (
          <p data-testid="intake-submit-error" className="mt-4 text-sm text-clay">
            Something didn&rsquo;t go through — your answers are saved. Please try once more.
          </p>
        )}
      </div>
    </div>
  );
};
